import React, { useState } from 'react';
import { 
  Box, 
  FormControl, 
  Select, 
  MenuItem, 
  SelectChangeEvent,
  Button,
  Stack,
  Typography
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { DateRange } from '@mui/icons-material';
import { DateRange as DateRangeType, DateRangeType as DateRangeTypeEnum } from '../../services/analyticsService';

interface DateRangePickerProps {
  dateRange: DateRangeType;
  onDateRangeChange: (dateRange: DateRangeType) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ dateRange, onDateRangeChange }) => {
  const [showCustomRange, setShowCustomRange] = useState(dateRange.date_range === 'custom');
  const [startDate, setStartDate] = useState<Date | null>(dateRange.startDate || null);
  const [endDate, setEndDate] = useState<Date | null>(dateRange.endDate || null);

  const handleDateRangeChange = (event: SelectChangeEvent) => {
    const newDateRange = event.target.value as DateRangeTypeEnum;
    console.log('DateRangePicker handleDateRangeChange:', {
      newDateRange,
      currentDateRange: dateRange
    });
    setShowCustomRange(newDateRange === 'custom');
    
    if (newDateRange !== 'custom') {
      const newRange = {
        date_range: newDateRange
      };
      console.log('DateRangePicker calling onDateRangeChange with:', newRange);
      onDateRangeChange(newRange);
    }
  };

  const handleApplyCustomRange = () => {
    if (startDate && endDate) {
      onDateRangeChange({
        date_range: 'custom',
        startDate,
        endDate
      });
    }
  };

  // Common style for date pickers
  const datePickerStyle = {
    width: '100%',
    padding: '8px 14px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white'
  };

  return (
    <Box className="flex flex-col md:flex-row items-start md:items-center gap-2 bg-white p-4 rounded-lg shadow-sm">
      <Stack direction="row" alignItems="center" spacing={1}>
        <DateRange className="text-gray-500" />
        <Typography variant="body2" className="text-gray-700 mr-2 whitespace-nowrap">
          Date Range:
        </Typography>
      </Stack>
      
      <FormControl size="small" className="min-w-[180px]">
        <Select
          value={dateRange.date_range}
          onChange={handleDateRangeChange}
          displayEmpty
          className="bg-white"
        >
          <MenuItem value="last_7_days">Last 7 Days</MenuItem>
          <MenuItem value="last_30_days">Last 30 Days</MenuItem>
          <MenuItem value="last_90_days">Last 90 Days</MenuItem>
          <MenuItem value="last_12_months">Last 12 Months</MenuItem>
          <MenuItem value="custom">Custom Range</MenuItem>
        </Select>
      </FormControl>
      
      {showCustomRange && (
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'flex-start', md: 'center' }}
          className="mt-2 md:mt-0"
        >
          <div>
            <DatePicker
              selected={startDate}
              onChange={(date: Date | null) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Start Date"
              dateFormat="MMMM d, yyyy"
              className="custom-datepicker"
              style={datePickerStyle}
              popperProps={{
                positionFixed: true
              }}
              popperModifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    enabled: true,
                    escapeWithReference: false,
                    boundary: window
                  },
                },
              ]}
            />
          </div>
          <div>
            <DatePicker
              selected={endDate}
              onChange={(date: Date | null) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="End Date"
              dateFormat="MMMM d, yyyy"
              className="custom-datepicker"
              style={datePickerStyle}
              popperProps={{
                positionFixed: true
              }}
              popperModifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    enabled: true,
                    escapeWithReference: false,
                    boundary: window
                  },
                },
              ]}
            />
          </div>
          <Button 
            variant="contained" 
            size="small" 
            onClick={handleApplyCustomRange}
            disabled={!startDate || !endDate}
          >
            Apply
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default DateRangePicker; 